import { useState } from "react";
import { ArrowRightLeft, Check, Zap, Activity } from "lucide-react";
import { useApp } from "@/context/AppCtx";

type TradingProvider = "helios" | "pumpportal";

interface TradingProviderSelectorProps {
  onProviderChange?: (provider: TradingProvider) => void;
  compact?: boolean;
}

export function TradingProviderSelector({ onProviderChange, compact = false }: TradingProviderSelectorProps) {
  const { heliosKey } = useApp();
  const [selectedProvider, setSelectedProvider] = useState<TradingProvider>("helios");
  const [isTransitioning, setIsTransitioning] = useState(false);

  const handleProviderSwitch = async (provider: TradingProvider) => {
    if (provider === selectedProvider) return;
    
    setIsTransitioning(true);
    
    // Simulate transition delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    setSelectedProvider(provider);
    setIsTransitioning(false);
    
    if (onProviderChange) {
      onProviderChange(provider);
    }
  };

  const providers = [
    {
      id: "helios" as TradingProvider,
      name: "Helios",
      icon: Zap,
      description: "Fast execution, reliable transactions",
      features: ["Optimal routing", "Trade confirmation", "Token supply"],
      recommended: true,
      configured: heliosKey.trim().length > 0
    },
    {
      id: "pumpportal" as TradingProvider,
      name: "PumpPortal",
      icon: Activity,
      description: "Lightning Trade API integration",
      features: ["Real-time data", "Direct execution", "Market data"],
      recommended: false,
      configured: true // Always available for public mode
    }
  ];

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        {providers.map((provider) => (
          <button
            key={provider.id}
            onClick={() => handleProviderSwitch(provider.id)}
            disabled={isTransitioning}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              selectedProvider === provider.id
                ? "bg-emerald-500/15 border border-emerald-500/30 text-emerald-300"
                : "bg-white/5 border border-white/10 text-white/60 hover:bg-white/10"
            } ${isTransitioning ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <provider.icon className="size-3.5" />
            <span>{provider.name}</span>
            {selectedProvider === provider.id && <Check className="size-3" />}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white">Trading Provider</h3>
        {isTransitioning && (
          <div className="flex items-center gap-2 text-amber-400 text-xs">
            <ArrowRightLeft className="size-3 animate-spin" />
            Switching...
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        {providers.map((provider) => (
          <button
            key={provider.id}
            onClick={() => handleProviderSwitch(provider.id)}
            disabled={isTransitioning}
            className={`relative p-4 rounded-xl border transition-all text-left ${
              selectedProvider === provider.id
                ? "bg-emerald-500/10 border-emerald-500/30"
                : "bg-white/5 border-white/10 hover:bg-white/10"
            } ${isTransitioning ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            {provider.recommended && (
              <div className="absolute top-2 right-2">
                <span className="text-[9px] font-bold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full">
                  Recommended
                </span>
              </div>
            )}

            <div className="flex items-center gap-2 mb-2">
              <provider.icon className={`size-4 ${selectedProvider === provider.id ? "text-emerald-400" : "text-white/60"}`} />
              <span className={`font-semibold ${selectedProvider === provider.id ? "text-white" : "text-white/80"}`}>
                {provider.name}
              </span>
            </div>

            <p className="text-[11px] text-white/50 mb-3">{provider.description}</p>

            <div className="space-y-1">
              {provider.features.map((feature) => (
                <div key={feature} className="flex items-center gap-1.5">
                  <div className={`size-1 rounded-full ${selectedProvider === provider.id ? "bg-emerald-400" : "bg-white/30"}`} />
                  <span className="text-[10px] text-white/40">{feature}</span>
                </div>
              ))}
            </div>

            {selectedProvider === provider.id && (
              <div className="absolute top-3 right-3">
                <Check className="size-4 text-emerald-400" />
              </div>
            )}

            <div className="mt-3 pt-3 border-t border-white/10">
              <div className="flex items-center gap-1.5">
                <div className={`size-1.5 rounded-full ${provider.configured ? "bg-emerald-400" : "bg-amber-400"}`} />
                <span className="text-[9px] text-white/40">
                  {provider.configured ? "Configured" : "Setup required"}
                </span>
              </div>
            </div>
          </button>
        ))}
      </div>

      <div className="bg-white/5 border border-white/10 rounded-lg p-3">
        <p className="text-[10px] text-white/50 leading-relaxed">
          <strong className="text-white/70">Current:</strong> {providers.find(p => p.id === selectedProvider)?.name} — {providers.find(p => p.id === selectedProvider)?.description}
        </p>
      </div>
    </div>
  );
}

export function useTradingProvider() {
  const [provider, setProvider] = useState<"helios" | "pumpportal">("helios");
  
  return {
    provider,
    setProvider,
    isHelios: provider === "helios",
    isPumpPortal: provider === "pumpportal"
  };
}
